// Family Compatibility Dashboard Widget
// Displays yogas, dasha overlaps, composite patterns, and transit sensitivity

import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Chip, Stack, Alert, Button, CircularProgress, LinearProgress } from '@mui/joy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { generateFamilyPatterns, FamilyMember, FamilyPatterns } from '../../utils/familyPatternDetector';

interface Props {
  members: FamilyMember[];
  familyYogas: Array<{ member: string; yogas: Array<{ yoga_name: string; type: string; strength: string }> }>;
  currentTransits?: Record<string, { house: number; sign: number }>;
  onGenerateMissingYogas?: () => Promise<void>;
  generatingYogas?: boolean;
  yogaGenerationProgress?: { current: number; total: number; currentMember: string } | null;
}

export default function FamilyCompatibilityWidget({ 
  members, 
  familyYogas, 
  currentTransits,
  onGenerateMissingYogas,
  generatingYogas = false,
  yogaGenerationProgress
}: Props) {
  const patterns: FamilyPatterns = useMemo(() => {
    if (members.length === 0) return {
      yogas: [],
      dashaOverlaps: [],
      compositePatterns: [],
      transitSensitivities: [],
      summary: { familyStrengths: [], familyChallenges: [], bestTimingWindows: [], supportNetwork: [] }
    };
    return generateFamilyPatterns(members, familyYogas, currentTransits);
  }, [members, familyYogas, currentTransits]);

  if (members.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            No family members to analyze. Add family members to see compatibility insights.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Generate Yogas Button */}
      {familyYogas.length === 0 && onGenerateMissingYogas && !generatingYogas && (
        <Alert variant="soft" color="primary">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Typography level="body-sm">
              📊 No yogas detected for family members yet. Generate yogas to unlock deeper insights.
            </Typography>
            {members.length > 0 && (
              <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                ℹ️ Note: Charts must be calculated for all family members first. Visit Birth Details tab, select each member, and click "Save & Calculate Chart".
              </Typography>
            )}
            <Button
              size="sm"
              variant="solid"
              color="primary"
              startDecorator={<AutoAwesomeIcon />}
              onClick={() => {
                onGenerateMissingYogas();
              }}
              sx={{ alignSelf: 'flex-start' }}
            >
              Generate Family Yogas
            </Button>
          </Box>
        </Alert>
      )}

      {/* Generation Progress */}
      {generatingYogas && yogaGenerationProgress && (
        <Card variant="soft" sx={{ bgcolor: 'primary.50' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size="sm" />
              <Typography level="title-sm">
                Generating yogas ({yogaGenerationProgress.current}/{yogaGenerationProgress.total})
              </Typography>
            </Box>
            {yogaGenerationProgress.currentMember && (
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Processing: {yogaGenerationProgress.currentMember}
              </Typography>
            )}
            <LinearProgress
              determinate
              value={(yogaGenerationProgress.current / yogaGenerationProgress.total) * 100}
              sx={{ mb: 1 }}
            />
          </CardContent>
        </Card>
      )}

      {/* Family Members Summary - Always show */}
      <Card variant="soft" sx={{ bgcolor: 'primary.50' }}>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            👨‍👩‍👧‍👦 Family Members ({members.length})
          </Typography>
          <Stack spacing={1}>
            {members.map((member, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography level="body-sm">
                  <strong>{member.full_name}</strong> ({member.relationship})
                </Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Summary Section */}
      <Card variant="soft" sx={{ bgcolor: 'primary.50' }}>
        <CardContent>
          <Typography level="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            🌟 Family Dynamics Overview
          </Typography>

          {patterns.summary.familyStrengths.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography level="title-sm" sx={{ mb: 1, color: 'success.600' }}>
                💪 Family Strengths:
              </Typography>
              <Stack spacing={0.5}>
                {patterns.summary.familyStrengths.map((strength, idx) => (
                  <Typography key={idx} level="body-sm" sx={{ pl: 2 }}>
                    • {strength}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {patterns.summary.familyChallenges.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography level="title-sm" sx={{ mb: 1, color: 'warning.600' }}>
                ⚠️ Areas Needing Support:
              </Typography>
              <Stack spacing={0.5}>
                {patterns.summary.familyChallenges.map((challenge, idx) => (
                  <Typography key={idx} level="body-sm" sx={{ pl: 2 }}>
                    • {challenge}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {patterns.summary.bestTimingWindows.length > 0 && (
            <Box>
              <Typography level="title-sm" sx={{ mb: 1, color: 'primary.600' }}>
                ⏰ Best Timing Windows:
              </Typography>
              <Stack spacing={0.5}>
                {patterns.summary.bestTimingWindows.map((window, idx) => (
                  <Typography key={idx} level="body-sm" sx={{ pl: 2 }}>
                    • {window}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {/* Show message if no patterns detected yet */}
          {patterns.summary.familyStrengths.length === 0 && 
           patterns.summary.familyChallenges.length === 0 && 
           patterns.summary.bestTimingWindows.length === 0 && (
            <Alert variant="soft" color="neutral">
              <Typography level="body-sm">
                Advanced pattern analysis requires yogas to be detected for family members. 
                Generate charts for each family member to enable deeper insights.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Support Network */}
      {patterns.summary.supportNetwork.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              🤝 Who Supports Who
            </Typography>
            <Stack spacing={1.5}>
              {patterns.summary.supportNetwork.map((link, idx) => (
                <Alert key={idx} variant="soft" color="success">
                  <Typography level="body-sm">
                    <strong>{link.supporter}</strong> can support <strong>{link.supported}</strong>
                  </Typography>
                  <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                    {link.reason}
                  </Typography>
                </Alert>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Yoga Patterns */}
      {patterns.yogas.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              🧘 Yoga Patterns
            </Typography>
            <Stack spacing={1.5}>
              {patterns.yogas.map((yoga, idx) => (
                <Box key={idx} sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                    <Chip size="sm" color={yoga.type === 'common' ? 'primary' : yoga.type === 'complementary' ? 'success' : 'warning'}>
                      {yoga.type}
                    </Chip>
                    <Typography level="title-sm">{yoga.yogaName}</Typography>
                  </Stack>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    Members: {yoga.members.join(', ')}
                  </Typography>
                  <Typography level="body-sm">{yoga.description}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Dasha Overlaps */}
      {patterns.dashaOverlaps.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              ⏳ Dasha Period Overlaps
            </Typography>
            <Stack spacing={1.5}>
              {patterns.dashaOverlaps.map((overlap, idx) => (
                <Alert
                  key={idx}
                  variant="soft"
                  color={overlap.type === 'favorable' ? 'success' : overlap.type === 'difficult' ? 'warning' : 'neutral'}
                >
                  <Typography level="title-sm">
                    {overlap.members.join(', ')}
                  </Typography>
                  <Typography level="body-xs" sx={{ mt: 0.5 }}>
                    {overlap.period.from} to {overlap.period.to}
                  </Typography>
                  <Typography level="body-sm" sx={{ mt: 0.5 }}>
                    {overlap.description}
                  </Typography>
                </Alert>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Composite Patterns */}
      {patterns.compositePatterns.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              🌈 Family Composite Patterns
            </Typography>
            <Stack spacing={1.5}>
              {patterns.compositePatterns.map((pattern, idx) => (
                <Box key={idx} sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 0.5, alignItems: 'center' }}>
                    <Typography level="title-sm">{pattern.name}</Typography>
                    <Chip size="sm" color={pattern.strength === 'dominant' ? 'success' : pattern.strength === 'balanced' ? 'neutral' : 'warning'}>
                      {pattern.strength}
                    </Chip>
                  </Stack>
                  <Typography level="body-xs" sx={{ mb: 0.5 }}>
                    Members: {pattern.members.join(', ')}
                  </Typography>
                  <Typography level="body-sm">{pattern.description}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Transit Sensitivities */}
      {patterns.transitSensitivities.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              🔄 Current Transit Impacts
            </Typography>
            <Stack spacing={1.5}>
              {patterns.transitSensitivities.map((transit, idx) => (
                <Box key={idx} sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                  <Typography level="title-sm" sx={{ mb: 0.5 }}>
                    {transit.planet} transiting House {transit.transitingHouse}
                  </Typography>
                  <Typography level="body-xs" sx={{ mb: 1, color: 'text.tertiary' }}>
                    Timeframe: {transit.timeframe}
                  </Typography>
                  <Stack spacing={0.5}>
                    {transit.affectedMembers.map((member, mIdx) => (
                      <Box key={mIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip size="sm" color={member.impact === 'positive' ? 'success' : 'warning'}>
                          {member.impact}
                        </Chip>
                        <Typography level="body-sm">
                          {member.name}: {member.reason}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
